import clsx from 'clsx';
import {
  ChevronDown,
  Download,
  KeyRound,
  LineChart,
  Lock,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { StatusBadge } from '../components/ui/display';
import { useAuth } from '../features/auth/AuthProvider';
import { useInstallPrompt } from '../pwa/install';
import { NAVIGATION, type NavItem, visibleNavigation } from './navigation';

function isActive(item: NavItem, pathname: string): boolean {
  if (item.to) return item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);
  return item.children?.some((c) => isActive(c, pathname)) ?? false;
}

function PhaseHint({ phase }: { phase?: number }) {
  if (!phase) return null;
  return (
    <span className="ml-auto rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
      P{phase}
    </span>
  );
}

/** Desktop sidebar: full width or a slim icon rail; remembered per browser. */
const COLLAPSED_KEY = 'mm.sidebarCollapsed';
function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1');
      } catch {
        // Storage unavailable (private mode): the choice lasts for this page only.
      }
      return !c;
    });
  return [collapsed, toggle] as const;
}

/**
 * Collapsed menu: hovering (or focusing / clicking) an icon shows its label or
 * submenu next to the rail. Rendered in a portal at a fixed position because
 * the scrolling menu list would clip anything positioned inside it.
 */
function useFlyout() {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const show = (el: HTMLElement) => {
    clearTimeout(timer.current);
    setRect(el.getBoundingClientRect());
  };
  const hide = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setRect(null), 150);
  };
  const keep = () => clearTimeout(timer.current);
  const close = () => {
    clearTimeout(timer.current);
    setRect(null);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  return { rect, show, hide, keep, close };
}

function Flyout({
  rect,
  onEnter,
  onLeave,
  children,
  label,
}: {
  rect: DOMRect;
  onEnter: () => void;
  onLeave: () => void;
  children?: ReactNode;
  label: string;
}) {
  return createPortal(
    <div
      role={children ? 'menu' : 'tooltip'}
      aria-label={label}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className="fixed z-50 pl-2"
      style={{ left: rect.right, top: rect.top }}
    >
      <div className="min-w-44 rounded-lg border border-slate-700 bg-slate-900 py-1.5 shadow-xl">
        <p
          className={clsx(
            'px-3 text-sm font-medium text-white',
            children ? 'pt-0.5 pb-1.5' : 'py-0.5',
          )}
        >
          {label}
        </p>
        {children && (
          <ul className="space-y-0.5 border-t border-slate-800 px-1.5 pt-1.5">{children}</ul>
        )}
      </div>
    </div>,
    document.body,
  );
}

function NavGroup({
  item,
  pathname,
  collapsed,
}: {
  item: NavItem;
  pathname: string;
  collapsed: boolean;
}) {
  const active = isActive(item, pathname);
  const [open, setOpen] = useState(active);
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);
  const Icon = item.icon;
  const fly = useFlyout();
  return (
    <li
      onMouseEnter={collapsed ? (e) => fly.show(e.currentTarget) : undefined}
      onMouseLeave={collapsed ? fly.hide : undefined}
    >
      <button
        type="button"
        onClick={(e) => {
          // In the icon rail, a click (touch) toggles the submenu flyout.
          if (collapsed) {
            if (fly.rect) fly.close();
            else fly.show(e.currentTarget);
          } else setOpen((o) => !o);
        }}
        onFocus={collapsed ? (e) => fly.show(e.currentTarget) : undefined}
        onKeyDown={collapsed ? (e) => e.key === 'Escape' && fly.close() : undefined}
        aria-haspopup={collapsed ? 'menu' : undefined}
        aria-expanded={collapsed ? !!fly.rect : open}
        className={clsx(
          'flex w-full items-center gap-3 rounded-lg py-2 text-sm font-medium',
          collapsed ? 'justify-center px-0' : 'px-3',
          active
            ? collapsed
              ? 'bg-slate-800 text-white'
              : 'text-white'
            : 'text-slate-300 hover:bg-slate-800 hover:text-white',
        )}
      >
        {Icon && <Icon className="size-4.5 shrink-0" aria-hidden />}
        {collapsed ? (
          <span className="sr-only">{item.label}</span>
        ) : (
          <>
            {item.label}
            <ChevronDown
              className={clsx('ml-auto size-4 transition-transform', open && 'rotate-180')}
            />
          </>
        )}
      </button>
      {collapsed && fly.rect && (
        <Flyout rect={fly.rect} label={item.label} onEnter={fly.keep} onLeave={fly.hide}>
          {item.children!.map((child) => (
            <li key={child.to}>
              <NavLink
                to={child.to!}
                role="menuitem"
                onClick={fly.close}
                onBlur={fly.hide}
                onFocus={fly.keep}
                className={({ isActive: a }) =>
                  clsx(
                    'flex items-center rounded-md px-2.5 py-1.5 text-sm',
                    a
                      ? 'bg-slate-800 font-medium text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                  )
                }
              >
                {child.label}
                <PhaseHint phase={child.phase} />
              </NavLink>
            </li>
          ))}
        </Flyout>
      )}
      {open && !collapsed && (
        <ul className="mt-1 ml-7 space-y-0.5 border-l border-slate-700 pl-2">
          {item.children!.map((child) => (
            <li key={child.to}>
              <NavLink
                to={child.to!}
                className={({ isActive: a }) =>
                  clsx(
                    'flex items-center rounded-md px-2.5 py-1.5 text-sm',
                    a ? 'bg-slate-800 font-medium text-white' : 'text-slate-400 hover:text-white',
                  )
                }
              >
                {child.label}
                <PhaseHint phase={child.phase} />
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Sidebar({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const items = visibleNavigation(NAVIGATION, can);
  return (
    <nav className="scrollbar-dark flex h-full flex-col bg-slate-900" aria-label="Main">
      <Link
        to="/"
        onClick={onNavigate}
        title={collapsed ? 'Marketing P/L' : undefined}
        className={clsx(
          'flex h-16 shrink-0 items-center gap-2.5',
          collapsed ? 'justify-center' : 'px-5',
        )}
      >
        <span className="rounded-lg bg-brand-600 p-1.5 text-white">
          <LineChart className="size-4.5" aria-hidden />
        </span>
        {!collapsed && <span className="font-semibold text-white">Marketing P/L</span>}
      </Link>
      <ul
        className={clsx('flex-1 space-y-0.5 overflow-y-auto pb-6', collapsed ? 'px-2' : 'px-3')}
        onClick={(e) => (e.target as HTMLElement).closest('a') && onNavigate?.()}
      >
        {items.map((item) =>
          item.children ? (
            <NavGroup key={item.label} item={item} pathname={pathname} collapsed={collapsed} />
          ) : (
            <NavItemLink key={item.to} item={item} collapsed={collapsed} />
          ),
        )}
      </ul>
    </nav>
  );
}

/** A top-level menu link; in the icon rail its name shows in a flyout on hover/focus. */
function NavItemLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const fly = useFlyout();
  return (
    <li
      onMouseEnter={collapsed ? (e) => fly.show(e.currentTarget) : undefined}
      onMouseLeave={collapsed ? fly.close : undefined}
    >
      <NavLink
        to={item.to!}
        end={item.to === '/'}
        onFocus={collapsed ? (e) => fly.show(e.currentTarget) : undefined}
        onBlur={collapsed ? fly.close : undefined}
        onClick={fly.close}
        className={({ isActive: a }) =>
          clsx(
            'flex items-center gap-3 rounded-lg py-2 text-sm font-medium',
            collapsed ? 'justify-center px-0' : 'px-3',
            a ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white',
          )
        }
      >
        {item.icon && <item.icon className="size-4.5 shrink-0" aria-hidden />}
        {collapsed ? (
          <span className="sr-only">{item.label}</span>
        ) : (
          <>
            {item.label}
            <PhaseHint phase={item.phase} />
          </>
        )}
      </NavLink>
      {collapsed && fly.rect && (
        <Flyout rect={fly.rect} label={item.label} onEnter={fly.keep} onLeave={fly.close} />
      )}
    </li>
  );
}

function UserMenu() {
  const { user, logout, lockNow, lockEnabled } = useAuth();
  const [open, setOpen] = useState(false);
  const install = useInstallPrompt();
  if (!user) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-100"
      >
        <span className="flex size-8 items-center justify-center rounded-full bg-brand-100 font-semibold text-brand-700">
          {user.name.charAt(0).toUpperCase()}
        </span>
        <span className="hidden text-left sm:block">
          <span className="block font-medium text-slate-900">{user.name}</span>
          <span className="block text-xs text-slate-500">{user.email}</span>
        </span>
        <ChevronDown className="size-4 text-slate-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className="absolute right-0 z-40 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg"
          >
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-xs text-slate-500">Role</span>
              <StatusBadge status={user.role} />
            </div>
            <Link
              to="/profile"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <KeyRound className="size-4" /> Account &amp; security
            </Link>
            {lockEnabled && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  void lockNow();
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <Lock className="size-4" /> Lock now
              </button>
            )}
            {install && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  void install();
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <Download className="size-4" /> Install app
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => void logout()}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
            >
              <LogOut className="size-4" /> Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const { pathname } = useLocation();
  useEffect(() => setMobileOpen(false), [pathname]);

  return (
    <div className="min-h-screen" data-sidebar={collapsed ? 'collapsed' : 'expanded'}>
      {/* Desktop sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-20 hidden transition-[width] duration-200 lg:block',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        <Sidebar collapsed={collapsed} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="relative h-full w-72 max-w-[85%]">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 -right-11 rounded-md bg-white p-1.5 text-slate-600"
              aria-label="Close menu"
            >
              <X className="size-5" />
            </button>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div
        className={clsx('transition-[padding] duration-200', collapsed ? 'lg:pl-16' : 'lg:pl-64')}
      >
        <header className="sticky top-0 z-10 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100 lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="size-5" />
          </button>
          <button
            type="button"
            onClick={toggleCollapsed}
            className="hidden rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700 lg:block"
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {collapsed ? (
              <PanelLeftOpen className="size-5" />
            ) : (
              <PanelLeftClose className="size-5" />
            )}
          </button>
          <div className="ml-auto">
            <UserMenu />
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
